import { WelcomePage } from "@src/pages/WelcomePage";
import { displayAdjudicationList, displayAdjudicationDetails } from "@src/pages/AdjudicationPage";
import { canAll, canAny } from "@src/security/permissions";

import { displayPersonList } from "@src/person/PersonList";
import {
  displayReadPerson,
  displayEditPerson,
} from "@src/forms/PersonForm";

import { displayVoterApplicationList } from "@src/lists/VoterApplicationList";
import { displayOperatorAccountRequestList } from "@src/users/OperatorAccountRequestList";
import { displayVoterList } from "@src/lists/VoterList";
import { displayVotingCenterList } from "@src/lists/VotingCenterList";
import { displayOperatorAccountRequest } from "@src/users/OperatorAccountRequest";

import {
  displayReadVoterApplication,
  displayEditVoterApplication,
} from "@src/forms/VoterApplicationForm";

import {
  displayReadVoter,
  displayEditVoter,
} from "@src/forms/VoterForm";

import {
  displayOperatorUser,
} from "@src/users/OperatorUser";

import {
  displayDiagnosticsList,
  displayDiagnosticsDetails
} from "@src/diagnostics";

import { displayElectoralGeography } from "@src/pages/ElectoralGeographyPage";
import { displayVotingCenterRedirection } from "@src/pages/VotingCenterRedirectionPage";
import { displayMaintenanceApplications } from "@src/pages/MaintenanceApplicationsPage";

import { displayOperatorUserList } from "@src/users/OperatorUserList";

import { displayReportingOverview } from "@src/pages/ReportingOverviewPage";
import { displayAdvancedReporting } from "@src/pages/ReportingPage";

import { displayBackofficeUserList } from "@auto/users/__BackofficeUserList";
import {
  displayBackofficeUserProfile
} from "@auto/users/__BackofficeUserProfile";

import { displayOperatorReport } from "@src/reporting/pages/OperatorReport.jsx";

import { displayTabletReports } from "@src/reporting/pages/TabletReports";

import { displayVotingCenterReports } from "@src/reporting/pages/VotingCenterReportsList";
import { displayVotingCenterReport } from "@src/reporting/pages/VotingCenterReport";

import {
  displayPreliminaryRolls,
  displayFinalRolls
} from "@src/voter-roll/VoterRollList";

import {
  displayPreliminaryRoll,
  displayFinalRoll
} from "@src/voter-roll/VoterRollDetails";

import {
  displayVoterRollPreview
} from "@src/voter-roll/VoterRollPreview";


import {
  displayPartnerUserList
} from "@auto/users/__PartnerUserList";

import {
  displayReadPartnerUser
} from "@auto/users/__PartnerUserForm";

import { displayApiDocumentation } from "@auto/doc/ApiDocumentationPage";

const accessDenied = () => ({
  uuid: "access-denied",
  view: () => (
    <div style={{ padding: 24 }}>
      <h1>403</h1>
      <div>Access denied</div>
    </div>
  )
});

const requireAll = (permissions, view) => (gui, params) => {
  if (!canAll(gui, permissions)) {
    return accessDenied();
  }

  return view(gui, params);
};

const requireAny = (permissions, view) => (gui, params) => {
  if (!canAny(gui, permissions)) {
    return accessDenied();
  }

  return view(gui, params);
};

export const routes = [
  {
    path: "/",
    view: (gui) => ({
      view: () => <WelcomePage gui={gui} />,
    }),
  },

  {
    path: "/electoral-geography/redirect",
    view: requireAll(["UPDATE_VOTING_CENTER"],
      (gui) => displayVotingCenterRedirection(gui)),
  },

  {
    path: "/electoral-geography",
    view: requireAny([
      "READ_ELECTORAL_GEOGRAPHY",
      "CREATE_DEPARTMENT", "UPDATE_DEPARTMENT",
      "CREATE_ARRONDISSEMENT", "UPDATE_ARRONDISSEMENT",
      "CREATE_COMMUNE", "UPDATE_COMMUNE",
      "CREATE_SECTION", "UPDATE_SECTION",
      "CREATE_CONSTITUENCY", "UPDATE_CONSTITUENCY",
      "CREATE_VOTING_CENTER", "UPDATE_VOTING_CENTER"
    ], (gui) => displayElectoralGeography(gui)),
  },

  // ---- NINU APPLICATION ADJUDICATION (under Personnes) ----
  { path: "/person/adjudications/:id",
    view: requireAll(["READ_PERSON"], (gui, params) =>
      displayAdjudicationDetails(gui, "NINU_CERTIFICATE", params.id)) },
  { path: "/person/adjudications",
    view: requireAll(["READ_PERSON"], (gui) =>
      displayAdjudicationList(gui, "NINU_CERTIFICATE")) },

  // ---- PERSON REGISTRY ----
  {
    path: "/person/new",
    view: requireAll(["CREATE_PERSON"], (gui) => displayEditPerson(gui, null)),
  },
  {
    path: "/person/:id/edit",
    view: requireAll(["READ_PERSON", "UPDATE_PERSON"], (gui, params) => displayEditPerson(gui, params.id)),
  },
  {
    path: "/person/:id",
    view: requireAll(["READ_PERSON"], (gui, params) => displayReadPerson(gui, params.id)),
  },
  {
    path: "/person",
    view: requireAll(["READ_PERSON"], (gui) => displayPersonList(gui)),
  },

  // ---- VOTER ADJUDICATION (under Inscription) ----
  { path: "/voter-application/adjudications/:id",
    view: requireAll(["READ_VOTER_APPLICATION"], (gui, params) =>
      displayAdjudicationDetails(gui, "VOTER_REGISTRATION", params.id)) },
  { path: "/voter-application/adjudications",
    view: requireAll(["READ_VOTER_APPLICATION", "SEARCH_VOTER_APPLICATION"], (gui) =>
      displayAdjudicationList(gui, "VOTER_REGISTRATION")) },

  // ---- VOTER APPLICATIONS ----
  {
    path: "/voter-application/new",
    view: requireAll(["CREATE_VOTER_APPLICATION"], (gui) => displayEditVoterApplication(gui, null)),
  },
  {
    path: "/voter-application/:id/edit",
    view: requireAll(["READ_VOTER_APPLICATION", "UPDATE_VOTER_APPLICATION"], (gui, params) => displayEditVoterApplication(gui, params.id)),
  },
  {
    path: "/voter-application/:id",
    view: requireAll(["READ_VOTER_APPLICATION"], (gui, params) => displayReadVoterApplication(gui, params.id)),
  },
  {
    path: "/voter-application",
    view: requireAll(["SEARCH_VOTER_APPLICATION", "COUNT_VOTER_APPLICATION"], (gui) => displayVoterApplicationList(gui)),
  },

  // ---- VOTERS ----
  {
    path: "/voter/new",
    view: requireAll(["CREATE_VOTER"], (gui) => displayEditVoter(gui, null)),
  },
  {
    path: "/voter/:id/edit",
    view: requireAll(["READ_VOTER", "UPDATE_VOTER"], (gui, params) => displayEditVoter(gui, params.id)),
  },
  {
    path: "/voter/:id",
    view: requireAll(["READ_VOTER"], (gui, params) => displayReadVoter(gui, params.id)),
  },
  {
    path: "/voter",
    view: requireAll(["SEARCH_VOTER", "COUNT_VOTER"], (gui) => displayVoterList(gui)),
  },

  // ---- USER ACCOUNT REQUESTS ----
  {
    path: "/operator-account-request/:gid",
    view: requireAll(["READ_OPERATOR_ACCOUNT_REQUEST"], (gui, params) => displayOperatorAccountRequest(gui, params.gid)),
  },
  {
    path: "/operator-account-request",
    view: requireAll(["READ_OPERATOR_ACCOUNT_REQUEST"], (gui) => displayOperatorAccountRequestList(gui)),
  },

  // ---- USER ACCOUNTS ----
  {
    path: "/operator-user/new",
    view: requireAll(["CREATE_OPERATOR_USER"], (gui) => displayOperatorUser(gui, null)),
  },
  {
    path: "/operator-user/:id/edit",
    view: requireAll(["READ_OPERATOR_USER", "UPDATE_OPERATOR_USER"], (gui, params) => displayOperatorUser(gui, params.id)),
  },
  {
    path: "/operator-user/:id",
    view: requireAll(["READ_OPERATOR_USER"], (gui, params) => displayOperatorUser(gui, params.id)),
  },
  {
    path: "/operator-user",
    view: requireAll(["READ_OPERATOR_USER"], (gui) => displayOperatorUserList(gui)),
  },

  // ---- ELECTORAL GEOGRAPHY: voting-center* ----
  {
    path: "/voting-center",
    view: requireAll(["COUNT_VOTING_CENTER"], (gui) => displayVotingCenterList(gui)),
  },

  // ---- VOTER ROLLS ----
  {
    path: "/voter-roll/preliminary/:id",
    view: requireAll(["READ_VOTER_ROLL"], (gui, params) => displayPreliminaryRoll(gui, params.id)),
  },
  {
    path: "/voter-roll/preliminary",
    view: requireAll(["SEARCH_VOTER_ROLL", "COUNT_VOTER_ROLL"], (gui) => displayPreliminaryRolls(gui)),
  },
  {
    path: "/voter-roll/final/:id",
    view: requireAll(["READ_VOTER_ROLL"], (gui, params) => displayFinalRoll(gui, params.id)),
  },
  {
    path: "/voter-roll/final",
    view: requireAll(["SEARCH_VOTER_ROLL", "COUNT_VOTER_ROLL"], (gui) => displayFinalRolls(gui)),
  },

  // ---- MAINTENANCE ----
  {
    path: "/maintenance/applications",
    view: requireAll(["SEARCH_VOTER_APPLICATION", "COUNT_VOTER_APPLICATION"],
      (gui) => displayMaintenanceApplications(gui)),
  },

  // ---- DIAGNOSTICS ----
  {
    path: "/diagnostics",
    view: requireAll(["READ_DIAGNOSTICS", "COUNT_DIAGNOSTICS"], (gui) => displayDiagnosticsList(gui)),
  },
  {
    path: "/diagnostics/:id",
    view: requireAll(["READ_DIAGNOSTICS"], (gui, params) => displayDiagnosticsDetails(gui, params.id)),
  },

  {
    path: "/reporting",
    view: requireAll(["READ_STATISTICAL_REPORTS"], (gui) => displayReportingOverview(gui)),
  },
  {
    path: "/reporting/advanced",
    view: requireAll(["READ_STATISTICAL_REPORTS"], (gui) => displayAdvancedReporting(gui)),
  },
  {
    path: "/reporting/operators/:ninu",
    view: requireAll(
      ["READ_OPERATIONAL_REPORTS"],
      (gui, params) => displayOperatorReport(gui, params.ninu)
    ),
  },
  {
    path: "/reporting/tablets",
    view: requireAll(["READ_OPERATIONAL_REPORTS"], (gui) => displayTabletReports(gui)),
  },

  {
    path: "/reporting/voting-centers/:gid/voter-roll-preview",
    view: requireAll(
      [
        "READ_OPERATIONAL_REPORTS",
        "SEARCH_VOTER",
        "COUNT_VOTER",
        "READ_PERSON"
      ],
      (gui, params) => displayVoterRollPreview(gui, params.gid)
    ),
  },
  {
    path: "/reporting/voting-centers/:gid",
    view: requireAll(
      ["READ_OPERATIONAL_REPORTS"],
      (gui, params) => displayVotingCenterReport(gui, params.gid)
    ),
  },
  {
    path: "/reporting/voting-centers",
    view: requireAll(
      ["READ_OPERATIONAL_REPORTS"],
      (gui) => displayVotingCenterReports(gui)
    ),
  },

  // ---- BACKOFFICE USERS ----
  {
    path: "/__backoffice-user/:id",
    view: (gui, params) =>
      requireAll(["READ_BACKOFFICE_USER"], (gui, params) => displayBackofficeUserProfile(gui, params.id))(gui, params),
  },
  {
    path: "/__backoffice-user",
    view: (gui) =>
      requireAll(["SEARCH_BACKOFFICE_USER", "COUNT_BACKOFFICE_USER"], (gui) => displayBackofficeUserList(gui))(gui),
  },
    // ---- API USERS ----
  {
    path: "/__partner-user/:id",
    view: (gui, params) =>
      requireAll(["READ_PARTNER_USER"], (gui, params) => displayReadPartnerUser(gui, params.id))(gui, params),
  },
  {
    path: "/__partner-users",
    view: (gui) =>
      requireAll(["SEARCH_PARTNER_USER", "COUNT_PARTNER_USER"], (gui) => displayPartnerUserList(gui))(gui),
  },
  // ---- DEVELOPER ----
  {
    path: "/developer/api",
    view: requireAll(
      ["READ_API_DOCUMENTATION"],
      (gui) => displayApiDocumentation(gui)
    ),
  },

];

export default routes;
